import React, { useEffect, useState } from 'react'
import {
  Input,
  BUTTON_THEME,
  CircularLoader,
  Button,
  Icon,
} from '@st-operations/uikit/src/v2/index.js'
import CheckboxTree from 'react-checkbox-tree'

import Select from '_components/select/index.jsx'
import User from '../../lib/User'
import Header from '_components/header/index.jsx'
import { useRetractSidebar } from '_hooks'
import { getCustomTaxonomies, updateCustomTaxonomies } from '_services/data-explorer'
import { getDaasClients } from '_services/daas-clients'
import { logErrorAsync } from '../../lib/utility'
import MediaMeasurementCampaignsStatusBadge from '../../components/media-measurement-campaign-status-badge'
import ColoredCheckbox from '_components/colored-checkbox'
import FileInput from '_components/file-input'
import csvToJSON from '_lib/csvToJson'

import './styles.scss'

const ENTITY = 'custom-taxonomies'

function convertValueToLabel(value) {
  return value
    .split('/')
    .map((segment) =>
      segment
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    )
    .join('/')
}

function stringToNested(input) {
  let output = {}
  // Create nested structure
  input.forEach((str) => {
    let path = str
      .split(/>|\//)
      .filter((f) => f)
      .map((node) => convertValueToLabel(node.trim()))
    let currentLevel = output

    path.forEach((item) => {
      if (!currentLevel[item]) {
        currentLevel[item] = {}
      }

      currentLevel = currentLevel[item]
    })
  })
  return output
}

// Convert nested object to required format
function convertNestedToCheckboxTreeFormatItem(obj, key, path = '') {
  const nodeValue = key.toLowerCase().replace(/\s/g, '_')
  const value = path ? `${path}/${nodeValue}` : nodeValue
  let newObj = { value, label: key }

  let children = Object.keys(obj)
  if (children.length > 0) {
    newObj.children = children.map((childKey) =>
      convertNestedToCheckboxTreeFormatItem(obj[childKey], childKey, value)
    )
  }

  return newObj
}

function convertNestedToCheckboxTreeFormat(obj) {
  return Object.keys(obj).map((key) => convertNestedToCheckboxTreeFormatItem(obj[key], key))
}

function getNodeValues(nodeList) {
  let values = []

  nodeList.forEach((node) => {
    values.push(node.value)

    if (node.children) {
      values = values.concat(getNodeValues(node.children))
    }
  })

  return values
}

const CustomTaxonomyPage = (props) => {
  const { id } = props.match.params

  const [data, setData] = useState()
  const [clients, setClients] = useState([])
  const [inRequest, setInRequest] = useState(false)
  const [file, setFile] = useState()
  const [nodes, setNodes] = useState([])
  const [expanded, setExpanded] = useState([])
  useRetractSidebar()

  useEffect(() => {
    const load = async (id) => {
      try {
        const data = await getCustomTaxonomies(id)
        setData(data)
        const categoriesLabels = data.categories?.map((c) => convertValueToLabel(c))
        const nestedCategories = stringToNested(categoriesLabels)
        setNodes(convertNestedToCheckboxTreeFormat(nestedCategories))
        setExpanded(data.categories)
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }

    const getClients = () => {
      getDaasClients().then((response) => {
        setClients(response.items)
      }).catch((error) => {
        console.error(error.message)
        toast.error(error.message)
      })
    }

    load(id)
    getClients()
  }, [id])

  useEffect(() => {
    if (!file) return
    const reader = new FileReader()
    reader.addEventListener(
      'load',
      () => {
        const json = csvToJSON(reader.result)

        if (json[0]?.category) {
          try {
            const nested = stringToNested(json.map((m) => m.category).filter((f) => f))
            const newNodes = convertNestedToCheckboxTreeFormat(nested)
            const allNodesSelection = getNodeValues(newNodes)
            // Enforce mutual exclusivity: categories upload clears composedTopics
            setData((data) => ({ ...data, categories: allNodesSelection, composedTopics: [] }))
            setExpanded(allNodesSelection)
            setNodes(newNodes)
          } catch (err) {
            toast.error(
              <>
                <p className="ml-4 text-lg font-semibold">Invalid file format!</p>
                <ul>
                  <li>
                    <strong>File Format:</strong> single column CSV
                  </li>
                  <li>
                    <strong>File suffix:</strong> csv
                  </li>
                  <li>
                    <strong>Header:</strong> category
                  </li>
                  <li>
                    <strong>One category per row</strong>
                  </li>
                  <li>
                    <strong>Multi-level category delimilter:</strong> /
                  </li>
                </ul>

                <ul className="-ml-6 list-none">
                  <li>
                    <strong>eg:</strong>
                  </li>
                  <li>category</li>
                  <li>/food_&_drink/beverages/soft_drinks</li>
                  <li>/food_&_drink/cooking_&_recipes</li>
                  <li>/food_&_drink/cooking_&_recipes/bbq</li>
                </ul>
              </>,
              { autoClose: 10000 }
            )
          }
        } else if (json[0]?.topics && json[0]?.keywords) {
          try {
            const composedTopics = json.map((entry) => ({
              topic: entry?.topics,
              keywords: entry?.keywords
                ?.split(',')
                ?.map((k) => k.trim())
                ?.filter(Boolean),
            }))
            // Enforce mutual exclusivity: topics upload clears categories and category tree state
            setData((data) => ({ ...data, composedTopics, categories: [] }))
            setNodes([])
            setExpanded([])
          } catch (err) {
            toast.error(
              <>
                <p className="ml-4 text-lg font-semibold">Invalid file format!</p>
                <ul>
                  <li>
                    <strong>File Format:</strong> double column CSV
                  </li>
                  <li>
                    <strong>File suffix:</strong> csv
                  </li>
                  <li>
                    <strong>Header:</strong> topics, keywords
                  </li>
                </ul>

                <ul className="-ml-6 list-none">
                  <li>
                    <strong>eg:</strong>
                  </li>
                  <li>topics, keywords,</li>
                  <li>topic1,"keyword1, keyword2",</li>
                  <li>topic2,"keyword3, keyword4"</li>
                </ul>
              </>,
              { autoClose: 10000 }
            )
          }
        } else {
          toast.error('Invalid file format!')
        }
      },
      false
    )
    reader.readAsText(file)
  }, [file])

  const handleSaveAs = async (status = 'draft') => {
    if (inRequest) return
    setInRequest('Updating')
    const { _id, email } = User.getProfile()

    const newData = {
      ...data,
      status,
      updated_at: new Date().toISOString(),
      updated_by: { _id, email },
    }

    try {
      await updateCustomTaxonomies(id, newData)
      toast.success('Update successful!')
      handleExit()
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
      setInRequest(false)
    }

  }

  const handleExit = () => {
    props.history.push(`/${ENTITY}`)
  }

  if (!data) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <CircularLoader />
      </div>
    )
  }

  return (
    <>
      <Header
        title={
          <div className="flex flex-row gap-4 items-center">
            <span>Custom Taxonomy</span>
            <MediaMeasurementCampaignsStatusBadge status={data.status} />
          </div>
        }
      >
        <Button theme={BUTTON_THEME.GHOST} onClick={handleExit}>
          Exit
        </Button>
        <If condition={data.status === 'draft'}>
          <Button theme={BUTTON_THEME.SECONDARY} onClick={() => handleSaveAs('draft')}>
            Save as Draft
          </Button>
        </If>
        <If condition={data.status === 'draft' || data.status === 'processed'}>
          <Button theme={BUTTON_THEME.PRIMARY} onClick={() => handleSaveAs('submitted')}>
            Submit
          </Button>
        </If>
      </Header>
      <div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Name"
            value={data.name}
            onChange={(value) => setData({ ...data, name: value })}
            description="Required"
          />
          <Select
            label="Client"
            value={data.clientName ? { value: data.clientName, label: data.clientName } : null}
            options={clients.map((c) => ({ value: c.name, label: c.name }))}
            onChange={(value) => setData({ ...data, clientName: value.value })}
            description="Required"
          />
        </div>
        {(() => {
          const hasCategories = Array.isArray(data.categories) && data.categories.length > 0
          const hasComposed = Array.isArray(data.composedTopics) && data.composedTopics.length > 0
          const hasAny = hasCategories || hasComposed

          const handleRemoveCategories = () => {
            setData((d) => ({ ...d, categories: [] }))
            setNodes([])
            setExpanded([])
            setFile(undefined)
          }

          const handleRemoveComposedTopics = () => {
            setData((d) => ({ ...d, composedTopics: [] }))
            setFile(undefined)
          }

          return (
            <>
              <If condition={!hasAny}>
                <div className="border border-solid border-gray-300 rounded mt-5 px-6 py-5 flex flex-col gap-3">
                  <div>
                    <h3 className="text-base font-semibold mb-1">Upload CSV (required)</h3>
                    <p className="text-sm text-gray-600">
                      Choose one format. Uploading one will replace any existing data.
                    </p>
                  </div>
                  <div className="flex flex-row gap-4">
                    <FileInput label="1 Column CSV (header: category)" onAddFile={setFile} />
                    <FileInput label="2 Column CSV (headers: topics, keywords)" onAddFile={setFile} />
                  </div>
                </div>
              </If>

              <If condition={hasCategories && hasComposed}>
                <div className="border border-solid border-yellow-400 bg-yellow-50 mt-5 px-6 py-4 rounded">
                  <div className="flex flex-row items-center justify-between gap-4">
                    <p className="text-sm">
                      Both Categories and Topics data are present. Please keep one type to continue.
                    </p>
                    <div className="flex flex-row gap-2">
                      <Button theme={BUTTON_THEME.SECONDARY} onClick={() => { setData((d) => ({ ...d, composedTopics: [] })); }}>
                        Keep Categories
                      </Button>
                      <Button theme={BUTTON_THEME.SECONDARY} onClick={() => { setData((d) => ({ ...d, categories: [] })); setNodes([]); setExpanded([]); }}>
                        Keep Topics
                      </Button>
                    </div>
                  </div>
                </div>
              </If>

              <If condition={hasCategories}>
                <div className="border border-solid border-gray-400 mt-5 px-6 py-4 rounded">
                  <div className="flex flex-row items-center justify-between mb-3">
                    <h3 className="text-base font-semibold">
                      Categories ({data.categories.length})
                    </h3>
                    <Button theme={BUTTON_THEME.GHOST} onClick={handleRemoveCategories}>
                      Remove
                    </Button>
                  </div>
                  <div className="url-similarity-seed__hide-checkbox">
                    <CheckboxTree
                      nodes={nodes}
                      expanded={expanded}
                      showNodeIcon={false}
                      optimisticToggle={true}
                      noCascade
                      onExpand={(expanded) => setExpanded(expanded)}
                      onClick={null}
                      icons={{
                        expandClose: (
                          <Icon
                            type="outline"
                            icon="angledown"
                            className="event-feeds-wizard-categories__tree-checkbox"
                            width={24}
                            height={24}
                            color="#3D3D3D"
                          />
                        ),
                        expandOpen: (
                          <Icon
                            type="outline"
                            className="event-feeds-wizard-categories__tree-checkbox"
                            icon="angleright"
                            width={24}
                            height={24}
                          />
                        ),
                        halfCheck: (
                          <ColoredCheckbox className="event-feeds-wizard-categories__tree-checkbox" />
                        ),
                        check: (
                          <ColoredCheckbox
                            className="event-feeds-wizard-categories__tree-checkbox"
                            checked
                          />
                        ),
                        uncheck: (
                          <ColoredCheckbox className="event-feeds-wizard-categories__tree-checkbox" />
                        ),
                      }}
                    />
                  </div>
                </div>
              </If>

              <If condition={hasComposed}>
                <div className="border border-solid border-gray-400 mt-5 px-6 py-4 rounded">
                  <div className="flex flex-row items-center justify-between mb-3">
                    <h3 className="text-base font-semibold">
                      Topics ({data.composedTopics.length})
                    </h3>
                    <Button theme={BUTTON_THEME.GHOST} onClick={handleRemoveComposedTopics}>
                      Remove
                    </Button>
                  </div>
                  <div className="url-similarity-seed__composed-topics">
                    <For each="composedTopic" of={data.composedTopics}>
                      <div key={composedTopic.topic} className="mb-3">
                        <h4 className="font-medium">{composedTopic.topic}</h4>
                        <ul className="list-disc ml-5">
                          <For each="keyword" of={composedTopic.keywords || []}>
                            <li key={keyword}>{keyword}</li>
                          </For>
                        </ul>
                      </div>
                    </For>
                  </div>
                </div>
              </If>
            </>
          )
        })()}
      </div>
    </>
  )
}

export default CustomTaxonomyPage
